module Meevo.Plugins.MerchantAccounts {
    "use strict";
    import TipAmountTypeEnum = Meevo.Model.TipAmountTypeEnum;

    /**
    * Main Section of Merchant Account
    */
    export class MainSection extends Meevo.Components.BaseClasses.DataMaintenanceSectionBase<Model.MerchantAccount> {
        /**
        * Base class override which defines what a DEFAULT model looks like.
        */
        private HTTP_PREFIX = 'http://';
        private HTTPS_PREFIX = 'https://';

        private _providers: Meevo.Model.MerchantProvider[] = [];
        private _providersPromise: ng.IPromise<void>;

        public useOnlyAlphaNumericCharacters = /^[a-zA-Z0-9]*$/;
        public useOnlyNumericCharacters = /^[0-9]*$/;
        public allowSpecialCharacters = /^[\w\W]*$/;
        public confirmPassword: string = "";
        public portAccessUrlProtocolType: string = '0';
        public hostProtocol: string = "https://"; // used on main.html
        public selectedProvider: Meevo.Model.MerchantProvider;
        public customizeStatementDescriptor: boolean;
        public statementDescriptorRegex = /^[A-Za-z0-9 .,'_\-?+*/]*$/;
        public isObjectStateSwitchDisabled: boolean = false;

        customAuditCriteria = {
            nounId: Model.NounEnum.MerchantAccount,
        };

        constructor(
            sectionId: number,
            private context: MerchantAccountsContext,
            displayNameContentKey: string,
            template: string,
            isPrimarySection: boolean,
            handlesOwnCrud: boolean,
            baseEndpoint: string,
            sectionEndpoint: string,
            loggingService: Meevo.Components.Logging.ILoggingService,
            dataService: Meevo.Components.BaseClasses.IDataService,
            protected identityService: Meevo.Components.Common.IIdentityService,
            noun: Meevo.Model.NounEnum = null,
            appSettings: Components.Common.IAppSettings,
            protected modalService: Meevo.Components.Modal.IModalService,
            sectionSecurityKey?: string
        ) {
            super(sectionId, displayNameContentKey, template, isPrimarySection, handlesOwnCrud, baseEndpoint, sectionEndpoint, loggingService, dataService,
                identityService, noun, appSettings, sectionSecurityKey);

            this.getMerchantProviders();
        }

        createSectionModelInstance(postAdd: (data: any) => any): void {
            this.dataService.getTemplate(this.baseEndpoint).then((response) => {
                this.model = response;
                // isDefault false from template will be interpreted as "use default"
                if (!this.model.isDefault) {
                    this.model.isDefault = undefined;
                }
                this.setSignatureCaptureEnabled();
                this.takeBackup();
                this.providerChanged(this.model.merchantProviderId);
            });
        }

        public sectionModelRetrieved(): void {
            this.setSignatureCaptureEnabled();
            this.takeBackup();
            this.postProviderChanged(this.model.merchantProviderId);
        }

        private setSignatureCaptureEnabled() {
            // isSignatureCapture undefined interpreted as "use hidden business info preference"
            if (this.model.isSignatureCaptureEnabled === undefined) {
                const enableSignatureCapture = this.appSettings.APP_BUSINFOSETTINGS.registerSettings.enableSignatureCaptureOnCreditCardDevice;
                this.model.isSignatureCaptureEnabled = enableSignatureCapture;
            }
        }

        private setSignaturePromptThresholdAmount() {
            if (!this.selectedProvider) {
                return;
            }

            // signaturePromptThresholdAmount null/undefined interpreted as "use merchant account provider setting"
            if (this.selectedProvider.hasSignatureCaptureSupport &&
                this.selectedProvider.hasConfigurableSignaturePromptThreshold) {
                    if (this.model.signaturePromptThresholdAmount === null ||
                        this.model.signaturePromptThresholdAmount === undefined) {
                        this.model.signaturePromptThresholdAmount = this.model.isSignatureCaptureEnabled
                            ? this.selectedProvider.signaturePromptThresholdAmount
                            : null;
                    }
            } else {
                this.model.signaturePromptThresholdAmount = null;
            }
        }

        getMerchantProviders() {
            const registerSettings = this.appSettings.APP_BUSINFOSETTINGS.registerSettings;

            this._providersPromise = this.dataService.customMethodData("/api/location/merchantProviders", "GET", null).then((response) => {
                const providers = [];

                const allProviders = <Meevo.Model.MerchantProvider[]>response.data;
                for (const provider of allProviders) {
                    const key = `enableMerchantProvider_${provider.id}`;
                    const setting = registerSettings[key] as boolean;
                    if (setting) {
                        providers.push(provider);
                    }
                }

                this._providers = providers;
            });
        }

        parseEnumToObject(source: any, target: Object): void {
            let key: any;

            let numbers = _.filter(Object.keys(source), (value: any) => {
                return _.isNumber(Number(value)) && !_.isNaN(Number(value));
            });

            for (let i = 0; i < numbers.length; i++) {
                key = numbers[i];

                target[i] = {
                    number: Number(key),
                    name: Meevo.Model.MerchantAccountProviderEnum[key]
                };
            }
        }

        get tipAmountTypeEnum(): any {
            return TipAmountTypeEnum;
        }

        get providers(): Meevo.Model.MerchantProvider[] {
            return this._providers;
        }

        isDefaultAccountVisible(providerId: number): boolean {
            if (this.isGiftCardAccount(providerId) || this.isCareCredit(providerId))
            {
                return false;
            }

            return true;
        }

        isAccountUpdaterVisible(): boolean {
            if (!this.selectedProvider) {
                return false;
            }

            return this.selectedProvider.hasAccountUpdaterSupport;
        }

        isHostedPageVisible(providerId: number): boolean {
            if (!this.selectedProvider) {
                return false;
            }

            if (this.isCareCredit(providerId)) {
                return false;
            }

            return this.selectedProvider.hasHostedPageSupport && this.selectedProvider.hasRegisterSupport;
        }

        isSignatureCaptureVisible(): boolean {
            if (!this.selectedProvider) {
                return false;
            }

            return this.selectedProvider.hasSignatureCaptureSupport;
        }

        isSignaturePromptThresholdVisible(): boolean {
            if (!this.selectedProvider || !this.model) {
                return false;
            }

            return this.selectedProvider.hasConfigurableSignaturePromptThreshold &&
                this.isSignatureCaptureVisible() &&
                this.model.isSignatureCaptureEnabled;
        }

        isWorldpayProvider(providerId: number): boolean {
            return this.isWorldpayAccount(providerId) || this.isWorldpayGC(providerId);
        }

        isWorldpayAccount(providerId: number): boolean {
            return providerId === Meevo.Model.MerchantAccountProviderEnum.Vantiv;
        }

        isWorldpayGC(providerId: number): boolean {
            return providerId === Meevo.Model.MerchantAccountProviderEnum.VantivGC;
        }

        isCardConnect(providerId: number): boolean {
            return providerId === Meevo.Model.MerchantAccountProviderEnum.CardConnect;
        }

        isPaymentSenseProvider(providerId: number): boolean {
            return this.isPaymentSense(providerId) || this.isConnectE(providerId);
        }

        isPaymentSense(providerId: number): boolean {
            return providerId === Meevo.Model.MerchantAccountProviderEnum.PaymentSense;
        }

        isConnectE(providerId: number): boolean {
            return providerId === Meevo.Model.MerchantAccountProviderEnum.ConnectE;
        }

        isCareCredit(providerId: number): boolean {
            return providerId === Meevo.Model.MerchantAccountProviderEnum.CareCredit;
        }

        isMeevoPayProvider(providerId: number): boolean {
            return this.context.isMeevoPayProvider(providerId);
        }

        isMeevoPay(providerId: number): boolean {
            return this.context.isMeevoPay(providerId);
        }

        isMeevoPayCanada(providerId: number): boolean {
            return this.context.isMeevoPayCanada(providerId);
        }

        isMeevoPayBalance(platformId: Meevo.Model.MerchantPlatformEnum): boolean {
            switch (platformId) {
                case Meevo.Model.MerchantPlatformEnum.Vias:
                    return false;
                case Meevo.Model.MerchantPlatformEnum.Balance:
                    return true;
                default:
                    return this.context.isMeevoPayBalanceModuleEnabled();
            }
        }

        isGiftCardAccount(providerId: number): boolean {
            return providerId === Meevo.Model.MerchantAccountProviderEnum.VantivGC;
        }

        providerHasTipOptionsSupport(providerId: number): boolean {
            return this.isWorldpayProvider(providerId) || this.isCardConnect(providerId) || this.isMeevoPayProvider(providerId);
        }

        getMerchantProvider(providerId: number): string {
            return Meevo.Model.MerchantAccountProviderEnum[providerId];
        }

        providerChanged(providerId: number): void {
            this.setIsInstantPayAvsCvvValidationEnabled();
            this.postProviderChanged(providerId);
        }

        postProviderChanged(providerId: number): void {
            if (!this.isMeevoPayProvider(this.model.merchantProviderId)) {
                this.model.statementDescriptor = null;
            }
            this.customizeStatementDescriptor = (this.model.statementDescriptor || "") !== "";

            if (this.isGiftCardAccount(providerId) || this.isCareCredit(providerId)) {
                this.model.isDefault = false;
            } else {
                if (this.originalEntity) {
                    this.model.isDefault = this.originalEntity.isDefault;
                }
            }

            if (this.isAdding) {
                if (this.isConnectE(providerId)) {
                    const provider = this.providers.find(el => el.id === providerId);
                    this.model.providerUrl = this.model.providerUrl || provider.defaultHostURL;
                    if (!this.model.portAccessUrl) {
                        this.model.portAccessUrl = provider.defaultProcessorsURL;
                        this.portAccessUrlProtocolType =
                            new URL(provider.defaultProcessorsURL).protocol + '//' === this.HTTP_PREFIX ? '0' : '1';
                    }
                    // only set default when field is undefined, otherwise keep the setting
                    if (this.model.isDefault === undefined) {
                        this.model.isDefault = true;
                    }
                }

                if (this.isCareCredit(providerId)) {
                    this.model.isTipEnable = false;
                }


                this.model.disableConfirmAmount = this.isWorldpayAccount(providerId);
            }

            this.setSignatureCaptureEnabled();

            this._providersPromise.then(() => {
                this.selectedProvider = _.findWhere(this._providers, { id: providerId });
            }).then(() => {
                this.setSignaturePromptThresholdAmount();
            });

            this.context.addRemoveSections(providerId);
        }

        showUrlControls(isContextReadonly: boolean) {
            return !isContextReadonly
                || Model.Guard.isNothing(this.model) // Adding
                || Model.Guard.isNothing(this.model.portAccessUrl) // No url value
                || this.model.portAccessUrl.length === 0;
        }

        portAccessUrlProtocolTypeChanged(): void {
            if (Meevo.Model.Guard.isSomething(this.model.portAccessUrl)) {
                // Reapply protocol prefix
                this.model.portAccessUrl = this.model.portAccessUrl.replace(this.HTTPS_PREFIX, '').replace(this.HTTP_PREFIX, '');
                let protocolPrefix = this.portAccessUrlProtocolType === '0' ? this.HTTP_PREFIX : this.HTTPS_PREFIX;
                this.model.portAccessUrl = `${protocolPrefix}${this.model.portAccessUrl}`;
            }
        }

        isPercentageTipOption(option: number): boolean {
            return option === TipAmountTypeEnum.Percentage;
        }

        enableUpdaterChanged(): void {
            if (this.model.merchantProviderId === Meevo.Model.MerchantAccountProviderEnum.Vantiv && this.model.isAccountUpdaterEnabled) {
                this.modalService.alert('LABEL_AUTOUPDATE_ACTIVATION_NOTIFICATION');
            }

            if (!this.model.isAccountUpdaterEnabled) {
                this.model.isUpdaterEnabledBillingEngine = false;
                this.model.isUpdaterEnabledExpiredCreditCard = false;
            }
        }

        signatureCaptureChanged(): void {
            // these apply immediately on checkbox state change:
            // - uncheck = revert to default provider capture threshold
            // - recheck = show default capture threshold (saves to merchant account)
            if (this.model.isSignatureCaptureEnabled &&
                this.selectedProvider.hasConfigurableSignaturePromptThreshold) {
                if (this.model.signaturePromptThresholdAmount === null ||
                    this.model.signaturePromptThresholdAmount === undefined) {
                    this.model.signaturePromptThresholdAmount = this.selectedProvider.signaturePromptThresholdAmount;
                }
            }
            else {
                this.model.signaturePromptThresholdAmount = null;
            }
        }

        public isMembershipCheckboxVisible(): boolean {
            if (!this.context.isSingleLocation()) {
                return false;
            }

            if (!this.selectedProvider.canCreateTokens) {
                return false;
            }

            return true;
        }

        public isUsedForMembershipTransactionsChanged() {
            if (!this.model ||
                !this.model.isUsedForMembershipTransactions) {
                return;
            }

            // if there is an already set default merchant account id and it matches
            // this merchant account id, then don't show the message
            // if there is no default merchant account id, then let checkbox get set
            if (this.model.defaultMembershipMerchantAccountId) {
                if (this.model.id == this.model.defaultMembershipMerchantAccountId) {
                    return;
                }

                this.modalService.confirm('MESSAGE_DEFAULT_MEMBERSHIP_CONFIRMATION').catch(() => {
                    this.model.isUsedForMembershipTransactions = false;
                });
            }
        }

        public isEGiftCheckboxVisible(): boolean {
            if (!this.context.isSingleLocation()) {
                return false;
            }

            if (!this.selectedProvider.hasHostedPageSupport) {
                return false;
            }

            return true;
        }

        public isUsedForEGiftTransactionsChanged() {
            if (!this.model ||
                !this.model.isUsedForEGiftTransactions) {
                return;
            }

            // if there is an already set default merchant account id and it matches
            // this merchant account id, then don't show the message
            // if there is no default merchant account id, then let checkbox get set
            if (this.model.defaultEGiftMerchantAccountId) {
                if (this.model.id == this.model.defaultEGiftMerchantAccountId) {
                    return;
                }

                this.modalService.confirm('MESSAGE_DEFAULT_EGIFT_CONFIRMATION').catch(() => {
                    this.model.isUsedForEGiftTransactions = false;
                });
            }
        }

        public isOnlineMembershipCheckboxVisible(): boolean {
            if (!this.model ||
                !this.model.isOnlineMembershipSalesEnabled) {
                return false;
            }

            if (!this.context.isMeevoPayProvider(this.selectedProvider.id)) {
                return false;
            }

            return true;
        }

        public isUsedForOnlineMembershipSalesChanged() {
            if (!this.model ||
                !this.model.isUsedForOnlineMembershipSales) {
                return;
            }

            // if there is an already set default merchant account id and it matches
            // this merchant account id, then don't show the message
            // if there is no default merchant account id, then let checkbox get set
            if (this.model.defaultOnlineMembershipMerchantAccountId) {
                if (this.model.id == this.model.defaultOnlineMembershipMerchantAccountId) {
                    return;
                }

                this.modalService.yellowConfirm('MESSAGE_DEFAULT_ONLINE_MEMBERSHIP_CONFIRMATION').catch(() => {
                    this.model.isUsedForOnlineMembershipSales = false;
                });
            }
        }

        enableTipsChanged(): void {
            if (this.model.isTipEnable && this.model.tipOptions.length === 0) {
                this.model.tipAmountTypeEnum = TipAmountTypeEnum.Percentage;

                this.model.tipOptions.push({
                    merchantAccountId: this.model.id,
                    tipOption: .10
                });
                this.model.tipOptions.push({
                    merchantAccountId: this.model.id,
                    tipOption: .15
                });
                this.model.tipOptions.push({
                    merchantAccountId: this.model.id,
                    tipOption: .20
                });
            } else if (!this.model.isTipEnable) {
                this.model.tipOptions = [];
            }
        }

        tipTypeChanged(): void {
            if (!this.isEditing || !this.model.isTipEnable) {
                return;
            }

            if (this.model.tipAmountTypeEnum === TipAmountTypeEnum.Percentage) {
                _.each(this.model.tipOptions, (tip: any) => {
                    tip.tipOption /= 100;
                });
            } else {
                _.each(this.model.tipOptions, (tip: any) => {
                    tip.tipOption *= 100;
                });
            }
        }

        addNewTipOption(): void {
            if (this.isReadonly) {
                return;
            }

            var vm = (<Meevo.Model.MerchantAccount>this.model);
            if (vm.tipOptions.length < 5) {
                var tip = { tipOption: vm.tipAmountTypeEnum === TipAmountTypeEnum.Percentage ? .01 : 1 };
                vm.tipOptions.push(<Meevo.Model.MerchantAccountTipOption>tip);
            }
        }

        deleteTipOption(amount: number): void {
            if (this.isReadonly) {
                return;
            }

            this.modalService.confirm("MESSAGE_DELETE_CONFIRMATION").then((confirmResult: boolean) => {
                if (confirmResult) {
                    var vm = (<Meevo.Model.MerchantAccount>this.model);
                    vm.tipOptions = _.reject(vm.tipOptions,
                        (tipElement: Meevo.Model.MerchantAccountTipOption) => {
                            return tipElement.tipOption === amount;
                        });
                }
            });
        }

        get isReadonly(): boolean {
            return this.mode === Meevo.Model.MaintenanceFormState.Readonly;
        }

        get isAdding(): boolean {
            return this.mode === Meevo.Model.MaintenanceFormState.Adding;
        }

        providerUrlChanged() {
            var vm = (<Meevo.Model.MerchantAccount>this.model);
            if (vm.providerUrl) {
                vm.providerUrl = vm.providerUrl.replace(this.HTTPS_PREFIX, '').replace(this.HTTP_PREFIX, '');
            }
        }

        checkValidationErrors(): boolean {
            if (!this.model) {
                return false;
            }

            // add provider-specific client-side validations here

            if (this.isMeevoPayProvider(this.model.merchantProviderId)) {
                if (!this.model.isUsedForOnlineMembershipSales &&
                    this.model.defaultOnlineMembershipMerchantAccountId == this.model.id
                ) {
                    this.modalService.alert('MESSAGE_ACTIVE_ONLINE_MEMBERSHIP_MERCHANT_ACCOUNT');
                    return false;
                }
            }

            return true;
        }

        isPreAuthValidateVisible(): boolean{
            if (!this.selectedProvider || !this.model) {
                return false;
            }

            return this.isHostedPageVisible(this.model.merchantProviderId) &&
                   this.selectedProvider.canEnablePreAuthForPaymentAccountCreation &&
                   this.model.isHostedPageEnabled;
        }

        preAuthEnabledChanged(): void {
            this.model.preAuthAmount = this.model.isPreAuthForPaymentAccountCreationEnabled ? 1 : 0;
        }

        public isInstantPaymentValidateVisible() {
            if (!this.selectedProvider || !this.model) {
                return false;
            }

            return this.isHostedPageVisible(this.model.merchantProviderId) &&
                   this.selectedProvider.canEnableInstantPayAvsCvvValidation &&
                   this.model.isHostedPageEnabled;
        }

        public canRestrictUnlinkedRefunds() {
            if (!this.model) {
                return false;
            }
            return this.isWorldpayAccount(this.model.merchantProviderId) || this.isCardConnect(this.model.merchantProviderId);
        }

        public canSeeHsaFsa() {
            if (!this.model) {
                return false;
            }
            return this.isWorldpayAccount(this.model.merchantProviderId) || this.isMeevoPay(this.model.merchantProviderId);
        }

        isHostedPageEnabledChanged() {
            this.setIsInstantPayAvsCvvValidationEnabled();
        }

        private setIsInstantPayAvsCvvValidationEnabled() {
            this.model.isInstantPayAvsCvvValidationEnabled = this.model.isHostedPageEnabled &&
                this.isWorldpayAccount(this.model.merchantProviderId);
        }

        customizeStatementDescriptorChanged(): void {
            if (!this.customizeStatementDescriptor) {
                this.model.statementDescriptor = null;
            }
        }

        public onObjectStateChanged() {
            const oldObjectState = this.model.objectState;
            if (oldObjectState !== Meevo.Model.ObjectStateEnum.Active) {
                return;
            }

            if (!this.isMeevoPayProvider(this.model.merchantProviderId)) {
                return;
            }

            this.isObjectStateSwitchDisabled = true;
            this.context.checkTokenMigrationInProgress()
                .then((inProgress: boolean) => {
                    if (inProgress) {
                        this.model.objectState = oldObjectState;
                    }
                })
                .finally(() => {
                    this.isObjectStateSwitchDisabled = false;
                });;
        }
    }
}